import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AgeGroup,
  Colors,
  resolveAgeGroup,
} from '@daas-consulting/production/shared/cross/age_groups_util';

/*
Neo: 0 días - 364 días - menor a 10 kg
Pediátrico: 1 año - 11 años - 10 a 35 kg
Adulto: 12 o más años - mayor a 35 kg
*/

// Clases Tailwind completas y literales: el content-scan de Tailwind detecta
// texto literal en archivos .ts, pero no puede resolver `fill-${color}-500`
// armado por interpolación en runtime.
const AGE_GROUP_FILL_COLOR: Record<string, string> = {
  [Colors.red]: 'fill-red-500',
  [Colors.pink]: 'fill-pink-500',
  [Colors.purple]: 'fill-purple-500',
  [Colors.indigo]: 'fill-indigo-500',
  [Colors.blue]: 'fill-blue-500',
  [Colors.cyan]: 'fill-cyan-500',
  [Colors.teal]: 'fill-teal-500',
  [Colors.green]: 'fill-green-500',
  [Colors.lime]: 'fill-lime-500',
  [Colors.yellow]: 'fill-yellow-500',
  [Colors.amber]: 'fill-amber-500',
  [Colors.orange]: 'fill-orange-500',
};

@Component({
  selector: 'daas-consulting-shared-layout-age-range',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './age-range.component.html',
  styleUrl: './age-range.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgeRangeComponent implements OnChanges {
  @Input() dateOfBirth: Date | null = null;
  @Input() weight: number | null = null;
  @Input() config: AgeGroup[] | null = [];
  @Input() ageGroup: AgeGroup | null = null;

  #color = 'fill-red-500';
  #name = '';
  #tooltip = '';

  get color(): string {
    return this.#color;
  }

  get name(): string {
    return this.#name;
  }

  get tooltip(): string {
    return this.#tooltip;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this._calculate();
  }

  private _calculate() {
    let ageGroup = null;
    const now = new Date();
    const weight = <number>this.weight || 0;
    const dateOfBirth = new Date(<Date>this.dateOfBirth);

    // Reinicia los valores.
    let name = 'S/N';
    let color = `fill-gray-500`;
    let tooltip = '';

    if (this.ageGroup) {
      ageGroup = this.ageGroup;
    } else {
      ageGroup = resolveAgeGroup({ dateOfBirth, now, config: this.config });
    }

    if (typeof ageGroup === 'object' && ageGroup !== null) {
      color = AGE_GROUP_FILL_COLOR[ageGroup.color] ?? 'fill-gray-500';
      if (typeof weight === 'number' && weight > ageGroup.maxWeight) {
        name = `${ageGroup.name} * `;
        tooltip = `Peso: ${weight} kg > ${ageGroup.maxWeight} kg`;
      } else {
        name = ageGroup.name;
      }
    } else {
      name = 'S/N';
      color = 'fill-gray-500';
      const info = JSON.stringify({
        dateOfBirth: this.dateOfBirth,
        weight: this.weight,
      });
      tooltip = `No se pudo determinar el grupo etario con los datos: ${info}`;
    }

    // Asigna los valores calculados.
    this.#name = name;
    this.#color = color;
    this.#tooltip = tooltip;
  }
}
